import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import React, { useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useCustomStore, UseFocus, useMountEffect } from '../../../hooks';
import { SelectDatePicker } from '../../Forms/datePicker';
import { InputText } from '../../Forms/InputText';
import search from './../../../assets/images/h-search.svg';

const DATE_PICKER_FORMAT = 'MM/DD/YYYY';

const normalizeDateForPicker = (value?: string) => {
  if (!value) {
    return '';
  }

  const parsed = moment(value, [DATE_PICKER_FORMAT, 'YYYY-MM-DD'], true);
  return parsed.isValid() ? parsed.format(DATE_PICKER_FORMAT) : '';
};

const toValidDate = (value?: string) => {
  if (!value) {
    return undefined;
  }

  const parsed = moment(value, [DATE_PICKER_FORMAT, 'YYYY-MM-DD'], true);
  return parsed.isValid() ? parsed.toDate() : undefined;
};

const ViolationsSearchForm: React.FC<any> = (props) => {
  const { classes, searchPage, onClose } = props;

  const [input1Ref, setInput1Focus] = UseFocus();
  const { violationsStore: store } = useCustomStore();
  const { control, handleSubmit, setValue, watch } = useForm<{
    createdDate: string;
    description: string;
    violationDateEnd: string;
    violationDateStart: string;
  }>({
    defaultValues: {
      createdDate: normalizeDateForPicker(store.SearchParams.createdDate),
      description: store.SearchParams.description || '',
      violationDateEnd: normalizeDateForPicker(
        store.SearchParams.violationDateEnd,
      ),
      violationDateStart: normalizeDateForPicker(
        store.SearchParams.violationDateStart,
      ),
    },
  });

  const createdDateValue = normalizeDateForPicker(
    store.SearchParams.createdDate,
  );

  const shouldShowCreatedDate = !!createdDateValue;

  //   const formValue = useMemo(() => getValues(), [getValues]);

  useMountEffect(setInput1Focus);

  const initSearch = useCallback(
    (data) => {
      if (
        !data.description &&
        !data.violationDateStart &&
        !data.violationDateEnd
      ) {
        return;
      }
      (async () => {
        store.setShowLoader(true);
        store.resetSortParams();
        store.resetParams();
        store.setSearchParams(data);
        await store.getList(data);

        onClose();
        store.setShowLoader(false);
      })();
    },
    [store, onClose],
  );

  const resetFilter = useCallback(() => {
    store.resetParams();
    store.resetSearchParams();
    store.resetSortParams();
    setValue('createdDate', '');
    setValue('description', '');
    setValue('violationDateStart', '');
    setValue('violationDateEnd', '');
    store.getList();
  }, [store, setValue]);
  return (
    <form onSubmit={handleSubmit(initSearch)} id={searchPage}>
      <FormControl error={false} fullWidth>
        <Typography
          component={'h3'}
          sx={{
            color: '#FFFFFF',
            fontFamily: 'FiraSans-Medium',
            fontSize: '15px',
            fontWeight: '500',
            height: '22px',
            letterSpacing: '1px',
            lineHeight: '22px',
            textTransform: 'capitalize',
          }}>
          Search Violation Description
        </Typography>

        <Grid className={classes.borderBottom}>
          <Grid container className={classes.searchBox}>
            <Grid item md={2} className={classes.dFlexJA}>
              <img className={classes.filterIcon} src={search} alt="search" />
            </Grid>
            <Grid item md={10}>
              <Controller
                control={control}
                defaultValue=""
                name="description"
                render={({
                  field: { onChange, value, name, ref },
                  formState,
                }) => {
                  return (
                    <InputText
                      inputRefObj={input1Ref}
                      name={name}
                      isRefreshValue={true}
                      // defaultValue={filter.name}
                      value={value}
                      placeholder="Search"
                      onChangeText={onChange}
                      isEditable={false}
                      className={classes.inputF}
                      type={'text'}
                      variant="outlined"
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </FormControl>

      <Typography component={'h3'} className={classes.filterLabel}>
        Violation Date
      </Typography>
      <div className={clsx(classes.borderBottom, classes.dateFilter)}>
        <FormControl className="date-control">
          <Typography component={'h6'} className="label">
            From:
          </Typography>
          <Grid container className={classes.searchBox}>
            <Grid item md={2} className={classes.dFlexJA}>
              <img className={classes.filterIcon} src={search} alt="search" />
            </Grid>
            <Grid item md={10} className="date-picker">
              <Controller
                control={control}
                defaultValue=""
                name="violationDateStart"
                render={({
                  field: { onChange, value, name, ref },
                  formState,
                }) => {
                  return (
                    <SelectDatePicker
                      value={value}
                      isRefreshValue
                      inputRefObj={ref}
                      name={name}
                      onChangeDate={(d) => {
                        const formattedValue = d
                          ? moment(d).format(DATE_PICKER_FORMAT)
                          : '';
                        onChange(formattedValue);
                      }}
                      className={classes.selectClassName}
                      maxDate={
                        watch('violationDateEnd')
                          ? toValidDate(watch('violationDateEnd'))
                          : undefined
                      }
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </FormControl>
        <FormControl className="date-control">
          <Typography component={'h6'} className="label">
            To:
          </Typography>

          <Grid container className={classes.searchBox}>
            <Grid item md={2} className={classes.dFlexJA}>
              <img className={classes.filterIcon} src={search} alt="search" />
            </Grid>
            <Grid item md={10} className="date-picker">
              <Controller
                control={control}
                defaultValue=""
                name="violationDateEnd"
                render={({
                  field: { onChange, value, name, ref },
                  formState,
                }) => {
                  return (
                    <SelectDatePicker
                      value={value}
                      isRefreshValue
                      inputRefObj={ref}
                      name={name}
                      onChangeDate={(d) => {
                        const formattedValue = d
                          ? moment(d).format(DATE_PICKER_FORMAT)
                          : '';
                        onChange(formattedValue);
                      }}
                      className={classes.selectClassName}
                      minDate={
                        watch('violationDateStart')
                          ? toValidDate(watch('violationDateStart'))
                          : undefined
                      }
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </FormControl>
      </div>

      {shouldShowCreatedDate && (
        <>
          <Typography component={'h3'} className={classes.filterLabel}>
            Created Date
          </Typography>
          <div className={clsx(classes.borderBottom)}>
            <FormControl className="date-control" fullWidth>
              <Grid container className={classes.searchBox}>
                <Grid item md={2} className={classes.dFlexJA}>
                  <img
                    className={classes.filterIcon}
                    src={search}
                    alt="search"
                  />
                </Grid>
                <Grid item md={10}>
                  <InputText
                    name="createdDate"
                    isRefreshValue={true}
                    value={createdDateValue}
                    isDisableTransparent={true}
                    className={classes.inputF}
                    type={'text'}
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </FormControl>
          </div>
        </>
      )}
      <Grid container style={{ padding: '0px !important' }}>
        <Grid item md={6}>
          <Typography component={'div'} className={classes.resetF}>
            <Button
              variant="contained"
              className={' d-flex-ja '.concat(classes.resetF)}
              // disabled
              type="button"
              onClick={resetFilter}>
              RESET
            </Button>
          </Typography>
        </Grid>
        <Grid item md={6}>
          <Typography
            component={'button'}
            // disabled={!formValue.description}
            className={classes.filterBtn}
            type="submit">
            SEARCH
          </Typography>
        </Grid>
      </Grid>
    </form>
  );
};

export default observer(ViolationsSearchForm);
